import React, { useEffect, useState } from 'react';
import {
  AccountInfoType,
  AdvertisingAccountType,
  CurrentAccountInfo,
} from '@amzn/aac-chrome-internal-api';
import { AccountDropdownBundleProvider } from 'src/providers/PantherLocalizationProvider';
import { AccountSwitcherPublisher } from 'src/commons/utils/metrics';
import { logger } from 'src/commons/utils/logger';
import { AccountInfoState, LoadingStatus } from 'src/commons/types';
import {
  getChromeData,
  getNavState,
  getNavbarInit,
  getAccountSwitcherInit,
} from 'src/commons/apis';
import { isBtswContext, isFromBtswWc, isFromTrim } from 'src/commons/utils';
import { useSelector } from 'react-redux';
import { selectCurrentAccountInfoFromHeader } from 'src/features/header/store/slice';
import { useIsFeatureEnabled } from 'src/commons/hooks';
import { WeblabedFeatures } from 'src/commons/constants';
import { getShadowHeaders } from 'src/commons/utils/shadowContext';
import { runAccountInfoShadow } from 'src/commons/utils/runShadowDiff';

const fallbackAccountInfo = {
  currentEntityType: '',
  accounts: [],
  currentAccountLink: '',
  currentAccountName: '',
  currentAccountType: AdvertisingAccountType.UNSUPPORTED,
  currentEntityId: '',
  currentMarketplace: '',
  currentMarketplaceId: '',
  accountTypeCounter: 0,
  accountCounter: 0,
  isAdmin: false,
  isManagerAPartner: false,
  hasAccessToDSP: false,
  hasAccessToSA: false,
  loadingStatus: 'success',
} as AccountInfoState;

// Exported so tests / Storybook can seed a populated context directly: the
// provider below only ever fills this from asynchronous fetches, which do not
// resolve in Storybook (it renders null there), so stories wrap the components
// in `<AccountDropdownContext.Provider value={...}>` to render populated states.
export const AccountDropdownContext =
  React.createContext<AccountInfoState>(fallbackAccountInfo);

export const useAccountDropdownContext = (): AccountInfoState => {
  const context = React.useContext(AccountDropdownContext);
  if (!context) {
    throw new Error(
      'AccountDropdownContext must be used with AccountDropdownProvider',
    );
  }
  return context;
};

export const AccountDropdownProvider: React.FC = ({ children }) => {
  const isInitialAccountInfoLoadedWithBTSW = isBtswContext();

  const [accountInfo, setAccountInfo] = useState<AccountInfoType>(
    {} as AccountInfoType,
  );

  // Switcher seed for the TrimService path (treatment only). Split across the two
  // Trim endpoints by role:
  // - isPrecomputed + nextToken ← accountswitcher-init: the nextToken cursor is
  //   the authoritative "more accounts exist" signal. It both selects the mode
  //   (non-null → server/whale source that paginates via /accounts; null → local
  //   source over the page-one accounts) and drives hasMore + search visibility.
  // - totalCount ← navbar-init.accountCount: the whole-inventory count, used only
  //   for telemetry (switcher-init.totalCount is deliberately NOT used).
  const [pagination, setPagination] = useState<{
    isPrecomputed: boolean;
    totalCount: number;
    nextToken: string | null;
  }>({ isPrecomputed: false, totalCount: 0, nextToken: null });

  const trimCurrentAccountInfo = useSelector(
    selectCurrentAccountInfoFromHeader,
  ) as AccountInfoType;
  const [detailedLoadingStatus, setDetailedLoadingStatus] =
    useState<LoadingStatus>('loading');

  const isShadowTreatment = useIsFeatureEnabled(
    WeblabedFeatures.MIGRATE_NAV_ACCOUNTSWITCHER_TO_TRIM_T2,
  );
  const isTrimNavMigrationEnabled =
    useIsFeatureEnabled(WeblabedFeatures.MIGRATE_NAV_ACCOUNTSWITCHER_TO_TRIM) ||
    isShadowTreatment;

  const accountTypeCounter: number =
    accountInfo && accountInfo.accounts ? accountInfo.accounts.length : 0;
  const accountCounter: number =
    accountInfo && accountInfo.accounts
      ? accountInfo.accounts.reduce(
          (acc, curr) => acc + curr.accounts.length,
          0,
        )
      : 0;

  const setAccountInfoCallback = (newAccountInfo?: AccountInfoType) => {
    // If getNavState call hasn't completed yet - set initialAccountInfo
    if (!accountInfo.accounts) {
      setAccountInfo((accountInfo) => ({
        ...accountInfo,
        ...newAccountInfo,
      }));
    }
    window.AACChrome.InternalAPI.AccountInfoHandler.getInstance().removeCallback(
      setAccountInfoCallback,
    );
    AccountSwitcherPublisher.publishCounterMonitor(
      'accountInfoFailure-getBTSWAccountInfo',
      0,
    );
  };

  // Load InitialAccountInfo from Trim
  const getTrimAccountInfo = () => {
    setAccountInfo((accountInfo) => ({
      ...accountInfo,
      ...trimCurrentAccountInfo,
    }));
  };

  // Load InitialAccountInfo from BTSW
  const getBTSWAccountInfo = () => {
    try {
      // the account info has been set in AccountInfoHandler and can be accessed
      // (i.e. async loading the header will yield this condition)
      const windowAccountInfo =
        window.AACChrome.InternalAPI.AccountInfoHandler.getInstance()
          .accountInfo;
      // accountInfo has not been set by the GlobalNav controller yet and a callback is needed
      if (!windowAccountInfo) {
        window.AACChrome.InternalAPI.AccountInfoHandler.getInstance().registerCallback(
          setAccountInfoCallback,
        );
      } else {
        setAccountInfo((accountInfo) => ({
          ...accountInfo,
          ...windowAccountInfo,
        }));
        AccountSwitcherPublisher.publishCounterMonitor(
          'accountInfoFailure-getBTSWAccountInfo',
          0,
        );
      }
    } catch (e: any) {
      AccountSwitcherPublisher.publishCounterMonitor(
        'accountInfoFailure-getBTSWAccountInfo',
        1,
      );
      logger.error('getBTSWAccountInfo failed', e);
    }
  };

  // Ajax load InitialAccountInfo from getChromeData api
  const fetchInitialAccountInto = async () => {
    try {
      const { chromeSharedData } = await getChromeData();
      // If getNavState call hasn't completed yet - set initialAccountInfo
      if (!accountInfo.accounts) {
        setAccountInfo((accountInfo) => ({
          ...accountInfo,
          ...chromeSharedData.currentAccountInfo,
        }));
      }
      AccountSwitcherPublisher.publishCounterMonitor(
        'accountInfoFailure-fetchInitialAccountInto',
        0,
      );
    } catch (e: any) {
      AccountSwitcherPublisher.publishCounterMonitor(
        'accountInfoFailure-fetchInitialAccountInto',
        1,
      );
      logger.error('fetchInitialAccountInto failed', e);
    }
  };

  // Treatment: the account tree comes from accountswitcher-init. It owns the
  // account list, the current-account fields, the page-one pagination token,
  // and the switcher's loading status — a failure here means the switcher has
  // no data (error). The explicit CurrentAccountInfo annotation replaces the
  // previous unchecked `as` cast: AccountSwitcherInit extends CurrentAccountInfo,
  // so if the hand-maintained Trim shape ever drops a field the UI consumes,
  // this assignment fails to compile instead of silently blanking.
  const loadSwitcherAccounts = async () => {
    const shadowOn = isShadowTreatment;
    const shadowHeaders = shadowOn ? getShadowHeaders() : undefined;
    try {
      const switcherInit = await getAccountSwitcherInit({
        headers: shadowHeaders,
      });
      setAccountInfo((accountInfo) => ({
        ...accountInfo,
        ...(switcherInit as CurrentAccountInfo),
      }));

      // A non-null nextToken means more pages exist server-side, so it selects
      // the data source: present → server (whale) source that paginates via
      // /accounts; null → local source over the page-one accounts.
      setPagination((prev) => ({
        ...prev,
        isPrecomputed: switcherInit.nextToken !== null,
        nextToken: switcherInit.nextToken,
      }));

      setDetailedLoadingStatus('success');
      AccountSwitcherPublisher.publishCounterMonitor(
        'accountInfoFailure-fetchDetailedAccountInfo',
        0,
      );
      if (shadowOn) {
        void runAccountInfoShadow(switcherInit);
      }
    } catch (e: any) {
      setDetailedLoadingStatus('error');
      AccountSwitcherPublisher.publishCounterMonitor(
        'accountInfoFailure-fetchDetailedAccountInfo',
        1,
      );
      logger.error('loadSwitcherAccounts failed', e);
      if (shadowOn) {
        void runAccountInfoShadow({ error: e });
      }
    }
  };

  // Treatment: navbar-init supplies ONLY the whole-inventory count (accountCount)
  // for the telemetry `totalCount` dimension — it no longer selects the mode
  // (the accountswitcher-init cursor does that). Fetched INDEPENDENTLY of the
  // account tree so a slow/failed navbar-init neither delays nor discards the
  // account list; if it fails alone the count simply stays at its default and
  // the switcher is unaffected.
  const loadNavbarCount = async () => {
    // Pass the shadow header here too. createCachedFetcher memoizes per query
    // string on window.__AACCHROME__, so whichever call fires FIRST decides
    // whether the header goes on the wire. Nav.tsx also calls getNavbarInit
    // with the header, but on isolated render trees (or when this provider
    // mounts first) that call could lose the race. Always pass the header on
    // T2 so the request is header-tagged regardless of ordering.
    const shadowHeaders = isShadowTreatment ? getShadowHeaders() : undefined;
    try {
      const navbarInit = await getNavbarInit({ headers: shadowHeaders });
      setPagination((prev) => ({
        ...prev,
        totalCount: navbarInit.accountCount,
      }));
    } catch (e: any) {
      // warn, not error: this is the same cached navbar-init request Nav.tsx
      // awaits, and Nav already logs its failure at error level ("failed to
      // load nav data"). The only consequence here is a default totalCount
      // telemetry dimension; the switcher itself is unaffected.
      logger.warn('loadNavbarCount failed', e);
    }
  };

  // Ajax load detailed AccountInfo: from getNavState (BTAS control) or, under
  // the Trim migration weblab, from two independent TrimService calls that do
  // not block each other. The CSRF token is NOT this provider's concern — Nav
  // owns __AAC_FIT_CSRF_TOKEN__ (from navState under control, from getCsrfToken
  // under treatment); the switcher only consumes account data here.
  const fetchDetailedAccountInfo = async () => {
    if (isTrimNavMigrationEnabled) {
      // Fire both without awaiting one before the other; each commits its own
      // slice of state as soon as it resolves.
      void loadSwitcherAccounts();
      void loadNavbarCount();
      return;
    }
    try {
      const detailedAccountInfo: CurrentAccountInfo = (await getNavState())
        .accountInfo;
      setDetailedLoadingStatus('success');
      setAccountInfo((accountInfo) => ({
        ...accountInfo,
        ...detailedAccountInfo,
      }));
      AccountSwitcherPublisher.publishCounterMonitor(
        'accountInfoFailure-fetchDetailedAccountInfo',
        0,
      );
    } catch (e: any) {
      setDetailedLoadingStatus('error');
      AccountSwitcherPublisher.publishCounterMonitor(
        'accountInfoFailure-fetchDetailedAccountInfo',
        1,
      );
      // warn, not error: getNavState() is the NavStateHandler's cached
      // request, the same one Nav.tsx awaits and already logs at error level
      // ("failed to load nav data"). The failure counter above still records
      // it for alarming.
      logger.warn('fetchDetailedAccountInfo failed', e);
    }
  };

  useEffect(() => {
    if (isInitialAccountInfoLoadedWithBTSW) {
      getBTSWAccountInfo();
    } else if (isFromTrim() || isFromBtswWc()) {
      getTrimAccountInfo();
    } else {
      fetchInitialAccountInto();
    }
    fetchDetailedAccountInfo();
  }, []);

  // do not render anything until account info is set
  if (Object.keys(accountInfo).length === 0) {
    return null;
  }

  return (
    <AccountDropdownBundleProvider>
      <AccountDropdownContext.Provider
        value={{
          ...accountInfo,
          accountTypeCounter,
          accountCounter,
          loadingStatus: detailedLoadingStatus,
          totalCount: pagination.totalCount,
          nextToken: pagination.nextToken,
          isPrecomputed: pagination.isPrecomputed,
        }}
      >
        {children}
      </AccountDropdownContext.Provider>
    </AccountDropdownBundleProvider>
  );
};
